import styled, { keyframes } from 'styled-components';

// Implementacao do design Login.html — layout 2-col estilo X/Twitter.
//
// Esquerda (brand-side): foto de fundo com overlay gradient + HIGRA mark
// no topo + hero "Bem-vindo de volta" com accent gradient + status row
// com 3 stats + footer. Esconde em <=1080 (mobile-first form).
//
// Direita (form-side): top com pill de idioma + heading com chip
// "Entrar" + campos + remember + submit + footer com versao.
//
// O LADO DO FORMULARIO (direita) e theme-aware: usa as CSS vars
// --cco-* (que o GlobalStyle troca por theme.title) pra renderizar
// light por padrao e dark quando o tema for dark. O BRAND SIDE
// (esquerda, foto + overlay) segue escuro de proposito — e um painel
// visual sobre foto, fica bem nos dois temas.
//
// Superficies translucidas dos campos: sobre fundo claro, branco-alpha
// fica invisivel; por isso usamos preto-alpha no light e branco-alpha
// no dark via estes helpers.
const softSurface = (props) =>
  props.theme.title === 'light'
    ? 'rgba(0, 0, 0, 0.02)'
    : 'rgba(255, 255, 255, 0.025)';
const softSurfaceHover = (props) =>
  props.theme.title === 'light'
    ? 'rgba(0, 0, 0, 0.04)'
    : 'rgba(255, 255, 255, 0.04)';

// ============================================================
// Shell
// ============================================================
export const Stage = styled.div`
  /* width 100vw + flex: 1 cobre os dois casos do shell do App:
     - App envolve em <div style="display:flex; flex:1"> — sem width
       explicito, o Stage ficaria do tamanho do conteudo. Forcamos
       100vw + flex:1 pra esticar.
     - O grid filho (1.05fr 1fr) depende de width definida pra
       distribuir corretamente. */
  width: 100vw;
  flex: 1;
  min-height: 100vh;
  display: grid;
  grid-template-columns: 1.05fr 1fr;
  background: var(--cco-bg);
  color: var(--cco-ink);
  font-family: 'Inter', ui-sans-serif, system-ui, -apple-system,
    sans-serif;
  -webkit-font-smoothing: antialiased;
  font-feature-settings: 'cv11', 'ss01';
  font-size: 14px;
  line-height: 1.4;

  @media (max-width: 1080px) {
    grid-template-columns: 1fr;
  }
`;

// ============================================================
// LEFT — brand side
// ============================================================
export const BrandSide = styled.aside`
  position: relative;
  overflow: hidden;
  background: #06090e;
  display: flex;
  flex-direction: column;
  padding: 48px 56px;
  min-height: 100vh;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    right: -1px;
    width: 1px;
    background: rgba(255, 255, 255, 0.14);
    z-index: 3;
  }

  @media (max-width: 1080px) {
    display: none;
  }
`;

export const BrandBg = styled.div`
  position: absolute;
  inset: 0;
  background-image: ${(props) => `url(${props.$src})`};
  background-size: cover;
  background-position: center;
  z-index: 0;
`;

export const BrandOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
  background: linear-gradient(
      90deg,
      rgba(6, 9, 14, 0.35) 0%,
      rgba(6, 9, 14, 0.2) 35%,
      rgba(6, 9, 14, 0.65) 100%
    ),
    radial-gradient(
      60% 70% at 28% 65%,
      rgba(28, 176, 216, 0.18),
      transparent 70%
    );
`;

export const BrandTop = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const BrandMark = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;

  > img {
    /* Logo Nexus — PNG 86x29. Renderiza no tamanho natural pra
       manter nitidez (1x). Em telas com DPI alta o png mantem
       qualidade aceitavel ate ~2x. */
    height: 36px;
    width: auto;
    display: block;
  }
`;

const heroFadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const BrandHero = styled.div`
  position: relative;
  z-index: 2;
  margin-top: auto;
  color: #fff;
  max-width: 560px;

  /* Fade-in suave no mount — chama atencao pro conteudo principal
     sem ser distrativo. Filhos ganham stagger via animation-delay. */
  > * {
    opacity: 0;
    animation: ${heroFadeIn} 600ms cubic-bezier(0.2, 0.7, 0.2, 1) forwards;
  }
  > *:nth-child(1) { animation-delay: 80ms; }
  > *:nth-child(2) { animation-delay: 160ms; }
  > *:nth-child(3) { animation-delay: 240ms; }
  > *:nth-child(4) { animation-delay: 320ms; }
`;

const pulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 3px rgba(28, 176, 216, 0.25); }
  50% { box-shadow: 0 0 0 6px rgba(28, 176, 216, 0.12); }
`;

export const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px;
  border-radius: 999px;
  background: rgba(28, 176, 216, 0.1);
  color: #1cb0d8;
  box-shadow: inset 0 0 0 1px rgba(28, 176, 216, 0.35);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  margin-bottom: 22px;

  > .pulse {
    width: 6px;
    height: 6px;
    border-radius: 999px;
    background: #1cb0d8;
    animation: ${pulse} 2.2s ease-in-out infinite;
  }
`;

export const HeroTitle = styled.h1`
  margin: 0 0 14px;
  /* clamp pra tipografia fluida: minimo 40px (telas pequenas),
     ideal 5.2vw (escala suave entre 1080-1920+), maximo 64px (cap
     pra nao virar gigante em monitores 4K). Substitui os 2 breakpoints
     discretos do design original. */
  font-size: clamp(40px, 5.2vw, 64px);
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.02;
  color: #fff;

  .accent {
    background: linear-gradient(90deg, #fff 0%, #6bc7e3 60%, #1cb0d8 100%);
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
  }
`;

export const HeroLead = styled.p`
  margin: 0 0 40px;
  font-size: 16px;
  color: rgba(231, 233, 234, 0.78);
  max-width: 480px;
  line-height: 1.55;
`;

// Status row 3-col com hairlines verticais entre as celulas. backdrop
// blur por cima do bg pra dar profundidade.
export const StatusRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0;
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 14px;
  overflow: hidden;
  background: rgba(10, 12, 16, 0.55);
  backdrop-filter: blur(10px);
`;

export const StatusCell = styled.div`
  padding: 14px 18px;
  border-right: 1px solid rgba(255, 255, 255, 0.14);
  display: flex;
  flex-direction: column;
  gap: 4px;

  &:last-child {
    border-right: 0;
  }

  > .k {
    font-size: 10.5px;
    font-weight: 700;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: rgba(231, 233, 234, 0.55);
  }
  > .v {
    font-size: 18px;
    font-weight: 700;
    color: #fff;
    font-variant-numeric: tabular-nums;
  }
  > .v .u {
    font-size: 11px;
    color: rgba(231, 233, 234, 0.55);
    font-weight: 500;
    margin-left: 4px;
  }
`;

export const BrandFoot = styled.div`
  position: relative;
  z-index: 2;
  margin-top: auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 36px;
  color: rgba(231, 233, 234, 0.5);
  font-size: 11.5px;
  letter-spacing: 0.06em;
`;

// ============================================================
// RIGHT — form side
// ============================================================
export const FormSide = styled.section`
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 32px 56px;
  min-height: 100vh;

  @media (max-width: 1080px) {
    padding: 32px 28px;
  }
  @media (max-width: 480px) {
    padding: 24px 18px;
  }
`;

export const FormTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  margin-bottom: auto;
`;

export const LangPill = styled.div`
  display: inline-flex;
  gap: 2px;
  padding: 3px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);

  > button {
    padding: 5px 12px;
    border-radius: 999px;
    font-size: 11.5px;
    font-weight: 700;
    letter-spacing: 0.06em;
    color: rgba(231, 233, 234, 0.5);
    background: transparent;
    border: 0;
    cursor: pointer;
    transition: all 0.15s;
    font-family: inherit;

    &.on {
      background: #e7e9ea;
      color: #0a0a0b;
    }
  }
`;

export const FormWrap = styled.div`
  align-self: center;
  width: 100%;
  max-width: 440px;
  /* margin auto top+bottom centra verticalmente dentro do FormSide
     (flex column). FormFoot perde margin-top:auto pra nao competir
     pelo espaco — fica direto na base via fluxo natural. */
  margin: auto auto;
  display: flex;
  flex-direction: column;
  gap: 28px;

  @media (max-width: 480px) {
    gap: 20px;
  }
`;

// ============================================================
// Heading
// ============================================================
export const FormHead = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const FormHeadLbl = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  align-self: flex-start;
  padding: 5px 12px;
  border-radius: 999px;
  background: rgba(28, 176, 216, 0.14);
  color: #1cb0d8;
  box-shadow: inset 0 0 0 1px rgba(28, 176, 216, 0.3);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;

  > svg {
    width: 14px;
    height: 14px;
  }
`;

export const FormTitle = styled.h2`
  margin: 0;
  font-size: 32px;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--cco-ink);

  @media (max-width: 480px) {
    font-size: 26px;
  }
`;

export const FormSub = styled.p`
  margin: 0;
  color: var(--cco-ink-mute);
  font-size: 13.5px;
  line-height: 1.5;

  > a {
    color: #1cb0d8;
    font-weight: 600;
    text-decoration: none;
  }
  > a:hover {
    text-decoration: underline;
  }
`;

// ============================================================
// Fields
// ============================================================
export const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;

  /* Estado de erro propagado via prop $error — borda vermelha + halo. */
  ${(props) =>
    props.$error &&
    `
    > .ctrl {
      border-color: #f4445c;
      background: rgba(244, 68, 92, 0.04);
      box-shadow: 0 0 0 4px rgba(244, 68, 92, 0.08);
    }
    > .ctrl svg.lead { color: #f4445c; }
  `}
`;

export const FieldLabel = styled.label`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--cco-ink-mute);

  > a {
    color: #1cb0d8;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: none;
    text-decoration: none;
  }
  > a:hover {
    text-decoration: underline;
  }
  > .hint {
    color: var(--cco-ink-faint);
    font-weight: 500;
    letter-spacing: 0.04em;
    text-transform: none;
  }
`;

export const Ctrl = styled.div.attrs({ className: 'ctrl' })`
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 16px;
  height: 52px;
  background: ${softSurface};
  border: 1px solid var(--cco-line);
  border-radius: 14px;
  transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;

  @media (max-width: 480px) {
    height: 48px;
    padding: 0 14px;
  }

  &:hover {
    background: ${softSurfaceHover};
  }
  &:focus-within {
    border-color: #1cb0d8;
    background: rgba(28, 176, 216, 0.04);
    box-shadow: 0 0 0 4px rgba(28, 176, 216, 0.1);
  }
  &:focus-within svg.lead {
    color: #1cb0d8;
  }

  > svg.lead {
    width: 16px;
    height: 16px;
    color: var(--cco-ink-mute);
    flex: none;
  }

  > input {
    flex: 1;
    background: transparent;
    border: 0;
    outline: 0;
    color: var(--cco-ink);
    font-size: 15px;
    font-weight: 500;
    padding: 0;
    min-width: 0;
    font-family: inherit;

    &::placeholder {
      color: var(--cco-ink-faint);
    }
  }
`;

export const ToggleEye = styled.button.attrs({
  type: 'button',
  className: 'toggle-eye',
})`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: transparent;
  color: var(--cco-ink-mute);
  border: 0;
  display: grid;
  place-items: center;
  flex: none;
  cursor: pointer;
  transition: color 0.15s, background 0.15s, box-shadow 0.15s;
  font-family: inherit;

  &:hover {
    color: var(--cco-ink);
    background: ${softSurfaceHover};
  }
  /* Foco visivel (teclado) — outline cyan compativel com keyboard nav.
     :focus-visible nao dispara em click do mouse, so em Tab/Shift+Tab. */
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(28, 176, 216, 0.5);
    color: #1cb0d8;
  }

  > svg {
    width: 18px;
    height: 18px;
  }
`;

export const Hint = styled.div`
  font-size: 11.5px;
  color: #f4445c;
  font-weight: 500;
  letter-spacing: 0.02em;
  display: flex;
  align-items: center;
  gap: 6px;

  > svg {
    width: 14px;
    height: 14px;
  }
`;

// Aviso de Caps Lock — tom amarelo (warning), nao vermelho (erro).
// Aparece so enquanto o estado capsLockOn=true. Tipografia compacta
// pra nao competir com mensagens de erro reais.
export const CapsLockHint = styled.div`
  font-size: 11.5px;
  color: #f1c349;
  font-weight: 500;
  letter-spacing: 0.02em;
  display: flex;
  align-items: center;
  gap: 6px;

  > svg {
    width: 14px;
    height: 14px;
  }
`;

// ============================================================
// Remember + Submit
// ============================================================
export const RememberRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
`;

export const Check = styled.button.attrs({ type: 'button' })`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  user-select: none;
  color: var(--cco-ink-mute);
  font-size: 13px;
  font-weight: 600;
  background: transparent;
  border: 0;
  padding: 0;
  font-family: inherit;
  transition: color 0.15s;
  border-radius: 6px;

  &:hover {
    color: var(--cco-ink);
  }
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(28, 176, 216, 0.4);
  }

  > .box {
    width: 18px;
    height: 18px;
    border-radius: 5px;
    flex: none;
    border: 1.5px solid var(--cco-line-strong);
    background: ${softSurface};
    display: grid;
    place-items: center;
    transition: all 0.15s;
  }
  > .box svg {
    width: 11px;
    height: 11px;
    opacity: 0;
    transition: opacity 0.15s;
  }

  &.on {
    color: #1cb0d8;
  }
  &.on > .box {
    background: #1cb0d8;
    border-color: #1cb0d8;
    color: #06151a;
  }
  &.on > .box svg {
    opacity: 1;
  }
`;

export const VisitorLink = styled.a`
  color: #71767b;
  font-size: 12.5px;
  font-weight: 600;
  text-decoration: none;
  transition: color 0.15s;

  &:hover {
    color: #1cb0d8;
  }
`;

export const Submit = styled.button.attrs({ type: 'submit' })`
  height: 52px;
  border-radius: 14px;
  background: #1cb0d8;
  color: #06151a;
  font-size: 13.5px;
  font-weight: 800;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  cursor: pointer;
  border: 0;
  transition: background 0.15s, filter 0.15s, transform 0.05s,
    box-shadow 0.15s;
  box-shadow: 0 12px 32px -10px rgba(28, 176, 216, 0.4);
  font-family: inherit;

  &:hover:not(:disabled) {
    background: #3ec0e0;
  }
  &:active:not(:disabled) {
    transform: translateY(1px);
  }
  &:focus-visible:not(:disabled) {
    outline: none;
    box-shadow: 0 12px 32px -10px rgba(28, 176, 216, 0.4),
      0 0 0 4px rgba(28, 176, 216, 0.5);
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    box-shadow: none;
  }

  > svg {
    width: 16px;
    height: 16px;
  }

  @media (max-width: 480px) {
    height: 48px;
    font-size: 12.5px;
    letter-spacing: 0.12em;
  }
`;

// ============================================================
// Footer
// ============================================================
export const FormFoot = styled.div`
  /* margin-top: auto removido — agora o FormWrap centraliza via
     margin: auto auto. FormFoot fica naturalmente abaixo do FormWrap
     (separado pelo margin-bottom:auto do FormWrap). */
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  color: var(--cco-ink-faint);
  font-size: 11.5px;
  letter-spacing: 0.04em;
  padding-top: 32px;
`;

export const Vers = styled.span`
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  color: var(--cco-ink-faint);
`;

// ============================================================
// Compat (mantidos pra nao quebrar testes ou imports legados)
// ============================================================
export const Container = Stage;
export const Column = FormSide;
export const ContentContainer = FormWrap;
export const Title = FormTitle;
export const Label = styled.span``;
export const ButtonContainer = styled.div``;
export const Wrapper = styled.div``;
export const Logo = styled.img`
  display: none;
`;
export const ForgotPasswordLink = styled.a`
  color: #1cb0d8;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;
export const customStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    borderRadius: '16px',
    width: '90%',
    height: '90%',
    background: '#0a0a0b',
    border: '1px solid rgba(255,255,255,0.14)',
  },
};
